import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

interface Props {
	/** The recommendation call came back empty — the rows hold the blank fallback. */
	hasFailed: boolean;
	/** A recommendation arrived after the rows were seeded, and the user is mid-edit. */
	hasUnapplied: boolean;
	isRetrying: boolean;
	onApply: () => void;
	onRetry: () => void;
}

/**
 * Says out loud that the targeting fields below are blank because the
 * recommendation did not arrive, and offers the way out.
 *
 * Without it the step is indistinguishable from "the AI had nothing to suggest":
 * the picker seeds the fallback pair with every field empty once its prefill
 * budget expires, which is exactly what a slow model call produced for days
 * before anyone could tell it apart from a working, unopinionated wizard.
 */
export const OnboardingRecommendationNotice: FunctionComponent<Props> = ({
	hasFailed,
	hasUnapplied,
	isRetrying,
	onApply,
	onRetry,
}) => {
	const { t } = useTranslation();

	if (!hasFailed && !hasUnapplied && !isRetrying) {
		return null;
	}

	const isOffer = hasUnapplied && !hasFailed;

	return (
		<div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
			<span className="text-xs text-text-meta">
				{t(`onboarding.lists.${isOffer ? "recommendationReady" : "recommendationFailed"}`)}
			</span>
			<button
				className="shrink-0 cursor-pointer text-xs font-semibold text-brand-light disabled:opacity-50"
				disabled={isRetrying}
				type="button"
				onClick={isOffer ? onApply : onRetry}
			>
				{t(
					`onboarding.lists.${isRetrying ? "recommendationRetrying" : isOffer ? "applyRecommendation" : "retryRecommendation"}`
				)}
			</button>
		</div>
	);
};
